import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';

import AntibodyEditModal from 'antibodies/actions/AntibodyEditModal';
import AntibodyDragWrapper from 'antibodies/antibody-map/AntibodyDragWrapper';
import {
  clampAntibodyPreviewCoordinatesToBoundingBox,
  getDefaultAntibodyPreviewCoordinates,
} from 'antibodies/antibody-map/AntibodyMapUtils';
import DomainPreview from 'antibodies/antibody-map/DomainPreview';
import useAntibodyMapState, {
  DEFAULT_ANTIBODY_DRAG_COORDINATE,
} from 'antibodies/antibody-map/useAntibodyMapState';
import {ANTIBODY_REST_PROPS_BY_ID} from 'antibodies/AntibodyConstants';
import AntibodyFeedbackBanner from 'antibodies/AntibodyFeedbackBanner';
import {Antibody as GQLAntibody, AntibodyWithVersion} from 'antibodies/AntibodyTypedefs';
import useAntibodyUIState from 'antibodies/hooks/useAntibodyUIState';
import {useAntibodyVersionQuery} from 'antibodies/hooks/useAntibodyVersionQuery';
import Antibody from 'antibodies/visualization/Antibody';
import {assert} from 'Asserts';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runModal from 'ui-library/runModal';

function assertIsNonNullishAntibodyAndVersion(
  antibody: GQLAntibody | null
): asserts antibody is AntibodyWithVersion {
  assert(antibody != null && antibody.version != null, 'Unexpected nullish antibody or version');
}

export interface AntibodyMapProps {
  antibodyId: string;
  isEditable: boolean;
}

export default function AntibodyMap({antibodyId, isEditable}: AntibodyMapProps): JSX.Element {
  const {
    state: {openVersionId},
  } = useAntibodyUIState(antibodyId);
  const {status, antibody} = useAntibodyVersionQuery(antibodyId, openVersionId);

  const {
    state: {
      dragState: {x, y},
      expandPreview,
      hoveredRef,
      selectedRef,
      showLabels,
      showMinimap,
    },
    setExpandPreview,
    setHoveredRef,
    setSelectedRef,
  } = useAntibodyMapState(antibodyId);

  const domainPreviewRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleExpandPreview = (): void => {
    let newX = x;
    let newY = y;
    if (wrapperRef.current != null) {
      // If we are expanding the preview for the first time (i.e., if we are not currently expanded and the
      // location of the antibody viz is at default)...
      if (
        !expandPreview &&
        x === DEFAULT_ANTIBODY_DRAG_COORDINATE &&
        y === DEFAULT_ANTIBODY_DRAG_COORDINATE
      ) {
        // ...put the antibody preview in the default location.
        [newX, newY] = getDefaultAntibodyPreviewCoordinates(wrapperRef.current);
      }
      // Clamp the coordinates just in case we've changed viewbox size since last viewing the expanded preview.
      [newX, newY] = clampAntibodyPreviewCoordinatesToBoundingBox(wrapperRef.current, newX, newY);
    }
    setExpandPreview(!expandPreview, newX, newY);
  };

  if (status === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (status === QueryResultType.ERROR || status === QueryResultType.SKIPPED) {
    return <span>Error loading antibody</span>;
  }

  assertIsNonNullishAntibodyAndVersion(antibody);

  const format = antibody.format;
  const restProps = ANTIBODY_REST_PROPS_BY_ID[format.id];
  const proteinIdsByRef = antibody.version.domains.reduce<Record<number, string>>((acc, domain) => {
    const {entity} = domain;
    if (entity == null) {
      return acc;
    }
    for (const ref of domain.refs) {
      // Collect all ids to power interactivity, even if they are inaccessible.
      acc[ref] = isInaccessible(entity) ? entity.invalidId : entity.id;
    }
    return acc;
  }, {});
  const inaccessibleProteinIdsByRef = antibody.version.domains.reduce<Record<number, string>>(
    (acc, domain) => {
      const {entity} = domain;
      if (entity != null && isInaccessible(entity)) {
        for (const ref of domain.refs) {
          acc[ref] = entity.invalidId;
        }
      }
      return acc;
    },
    {}
  );

  function openEditModal(): void {
    // antibody and its version are guaranteed to be non-null because of assertIsNonNullishAntibody before rendering
    runModal(AntibodyEditModal, {antibody: antibody as AntibodyWithVersion});
  }

  return (
    <div
      className={css(styles.wrapper)}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      ref={wrapperRef}
    >
      <AntibodyFeedbackBanner />
      <Button
        className={css(styles.editButton)}
        text="Edit"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        icon="edit"
        size={ButtonSize.MEDIUM}
        onClick={openEditModal}
        disabled={!isEditable}
      />
      {(!expandPreview || showMinimap) && (
        <AntibodyDragWrapper antibodyId={antibodyId} domainPreviewRef={domainPreviewRef}>
          <Antibody
            format={format}
            interactivity={{
              hoveredRef,
              setHoveredRef,
              selectedRef,
              setSelectedRef,
              proteinIdsByRef,
              inaccessibleProteinIdsByRef,
            }}
            {...restProps}
            showLabels={showLabels}
          />
        </AntibodyDragWrapper>
      )}
      {selectedRef != null && (
        <div
          className={css(expandPreview ? styles.previewWrapperExpanded : styles.previewWrapper)}
          ref={domainPreviewRef}
        >
          <DomainPreview
            antibodyId={antibodyId}
            selectedProteinId={proteinIdsByRef[selectedRef]}
            toggleExpandPreview={toggleExpandPreview}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    userSelect: 'none',
  },
  editButton: {
    width: 'fit-content',
    marginLeft: 'auto',
    marginTop: 8,
    marginBottom: 32,
  },
  previewWrapper: {
    flex: '0 0 280px',
    minHeight: 0,
    height: '280px',
  },
  previewWrapperExpanded: {
    flex: '1 0 auto',
    minHeight: 0,
  },
});
